import React, { useEffect, useState } from 'react';
import { View, Text, TextInput, Platform, Pressable, ScrollView } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
// @ts-ignore
import SerialCameraAddon from '../components/ocr/SerialCameraAddon';
import { MaterialIcons } from '@expo/vector-icons';
import Svg, { Line } from 'react-native-svg';
import { addChargerToPartner, getPartnerChargers } from '../services/partner-service';
import { isSuccessful } from '../services/api-requests';
import { notificationService } from '../services/notification-service';
import PartnerChargerList from '../components/partner/PartnerChargerList';
import PartnerInvitationNotification from '../components/partner/PartnerInvitationNotification';
import { useNavigation } from '@react-navigation/native';
import { getPartnerTermsStatus } from '../services/partner-terms-service';
import { ChargerAttributes } from '../models/charger-model';
import { HeaderWithMenu } from '../components/menu';

const PartnerPortal: React.FC = () => {
    const navigation = useNavigation<any>();
    const [serial, setSerial] = React.useState<string>('');
    const [refreshKey, setRefreshKey] = React.useState<number>(0);
    const [showInvitationPopup, setShowInvitationPopup] = useState(false);
    const [hasCheckedInvitation, setHasCheckedInvitation] = useState(false);
    const [hasSplitPartnership, setHasSplitPartnership] = useState(false);

    useEffect(() => {
        checkForPendingInvitations();
    }, [refreshKey]);

    const checkForPendingInvitations = async () => {
        if (hasCheckedInvitation) return; // Only check once per session
        
        try {
            // First, fetch partner chargers to check if they have split partnerships
            const chargersResponse = await getPartnerChargers();
            if (isSuccessful(chargersResponse.status) && chargersResponse.payload) {
                const chargers = (chargersResponse.payload as any)?.chargers || [];
                
                // Check if user has any charger with split permission
                const hasSplitChargers = chargers.some((c: ChargerAttributes) => 
                    c.permissions?.includes('split')
                );

                if (hasSplitChargers) {
                    setHasSplitPartnership(true);
                    
                    // Only check terms status if user has split chargers
                    const termsResponse = await getPartnerTermsStatus('SPLIT_PARTNER_TERMS');
                    const termsAccepted = termsResponse.payload?.hasAgreement || false;

                    // If terms not accepted, show popup
                    if (!termsAccepted) {
                        setShowInvitationPopup(true);
                    }
                }
            }
            
            setHasCheckedInvitation(true);
        } catch (error) {
            console.error('Error checking for pending invitations:', error);
            setHasCheckedInvitation(true);
        }
    };

    const handleReviewTerms = () => {
        setShowInvitationPopup(false);
        // Navigate to split partnerships page
        navigation.navigate('SplitPartnerships');
    };

    const handleDismissInvitation = () => {
        setShowInvitationPopup(false);
        setHasCheckedInvitation(true);
    };

    return (
        <View style={{ flex: 1, backgroundColor: '#1A1D21' }}>
            <SafeAreaView style={{ flex: 1 }} edges={['top']}>
                {/* Hamburger Menu Header */}
                <HeaderWithMenu forceHamburger title="Partner Portal" insideSafeArea />
                
                <ScrollView 
                    style={{ flex: 1 }}
                    contentContainerStyle={{ padding: 16, paddingBottom: 32 }}
                    showsVerticalScrollIndicator={false}
                >
                <Text style={{ color: '#8D8E90', marginBottom: 8 }}>Add Chargers</Text>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, backgroundColor: '#F1F1F1', borderRadius: 25, paddingHorizontal: 12, paddingVertical: 8 }}>
                    {serial.trim().length > 4 && (
                        <Pressable
                            onPress={async () => {
                                const unit_serial = serial.trim();
                                try {
                                    const resp = await addChargerToPartner({ manufacturer_name: 'Emporia', unit_serial });
                                    if (isSuccessful(resp.status)) {
                                        notificationService.sendNotification('success', 'Charger added');
                                        setSerial('');
                                        setRefreshKey(k => k + 1);
                                    } else {
                                        const msg = (resp.payload as any)?.message || 'Failed to add charger';
                                        notificationService.sendNotification('error', msg);
                                    }
                                } catch (e: any) {
                                    notificationService.sendNotification('error', e?.message || 'Network error');
                                }
                            }}
                            style={{ padding: 6 }}
                        >
                            <MaterialIcons name="add-circle" size={22} color="#51B868" />
                        </Pressable>
                    )}
                    <TextInput
                        value={serial}
                        onChangeText={setSerial}
                        placeholder="Enter serial number or scan QR code."
                        placeholderTextColor="#636363"
                        // autoCapitalize="characters"
                        style={{ flex: 1, color: '#1A1D21', paddingVertical: 6 }}
                    />
                    <SerialCameraAddon
                        manufacturer_name={'Emporia'}
                        iconSize={24}
                        iconColor="#636363"
                        onDetected={(detected: string) => setSerial(detected)}
                        onAddedSuccess={() => setRefreshKey(k => k + 1)}
                        onClosed={() => setRefreshKey(k => k + 1)}
                    />
                </View>
                
                {/* Dashboard Buttons */}
                {hasSplitPartnership && (
                    <View style={{ flexDirection: 'row', gap: 12, marginTop: 12, marginBottom: 12 }}>
                        <Pressable
                            onPress={() => navigation.navigate('SplitPartnerships')}
                            style={({ pressed }) => ({
                                backgroundColor: pressed ? '#F97316' : '#FFB800',
                                borderRadius: 12,
                                padding: 12,
                                flexDirection: 'row',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 8,
                                flex: 1,
                            })}
                        >
                            <MaterialIcons name="handshake" size={20} color="#FFFFFF" />
                            <Text style={{ color: '#FFFFFF', fontSize: 14, fontWeight: '600' }}>
                                Split Partnerships
                            </Text>
                        </Pressable>
                    </View>
                )}

                <View style={{ width: '100%', height: 1, marginTop: 8, marginBottom: 6 }}>
                    <Svg width="100%" height={1}>
                        <Line 
                            x1="0" y1="0.5" x2="100%" y2="0.5" 
                            stroke="#636363" 
                            strokeWidth={2} 
                            strokeDasharray="2 4" 
                            strokeLinecap="round" 
                        />
                    </Svg>
                </View>
                <Text style={{ color: '#FFFFFF', fontSize: 16, fontWeight: '600', marginBottom: 4 }}>My Chargers</Text>
                {/* Partner chargers list */}
                <PartnerChargerList refreshKey={refreshKey} />
            </ScrollView>
            
                {/* Invitation Notification Modal */}
                <PartnerInvitationNotification
                    visible={showInvitationPopup}
                    charger={null}
                    onReviewTerms={handleReviewTerms}
                    onDismiss={handleDismissInvitation}
                />
            </SafeAreaView>
        </View>
    );
};

export default PartnerPortal;


